import { Router } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	HttpClient,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { tap } from 'rxjs/operators';

import { AuthGoogleService } from './auth-google/auth-google.service';
import { ENVIRONMENT, Environment } from '@shared/environment';
import { EndPointService } from '@shared/services';
import { EmailAuthenticationService } from '@shared/services';

import moment from 'moment';
import { ChatService } from '../services/chat.service';
import { StorageHelperService } from '@shared/services';

const GOOGLE_AUTH_ERROR_MESSAGE = "Haven't signed in google apis.";

const EMAIL_CALENDAR_URL_PATTERNS = [
	'/api/emails/',
	'/api/googleapis/',
	'/api/calendar/',
	'/api/journal/',
] as const;

@Injectable()
export class MyHttpInterceptor implements HttpInterceptor {
	private readonly env = inject<Environment>(ENVIRONMENT);
	private readonly http = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly authGoogleService = inject(AuthGoogleService);
	private readonly endPointService = inject(EndPointService);
	private readonly emailService = inject(EmailAuthenticationService);
	private readonly snackBar = inject(MatSnackBar);
	private readonly message = inject(ChatService);
	private readonly storageHelper = inject(StorageHelperService);

	apiUrl;
	companyName = this.env.company;
	chatSignalr;
	private googleAuthDialogDismissed = false;
	private googleAuthDialogOpen = false;
	ggSync;
	private GOOGLE_ALREADY_LOGGED = 'You already logged in google apis.';

	constructor() {
		this.endPointService.companySource.subscribe(data => {
			this.apiUrl = data.apiUrl;
			this.chatSignalr = data.chatSignalr;
		});

		this.companyName = this.env.company;
	}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		return from(this.handle(req, next));
	}

	async handle(req: HttpRequest<any>, next: HttpHandler) {
		const authorization = (await this.storageHelper.getItem('token')) || '';
		const storedCode = await this.storageHelper.getItem('companyCode');
		let companyCode = storedCode || this.endPointService.extractSubdomain() || '';

		// switch to mobile
		// companyCode = 'test';
		const platform = Capacitor.getPlatform();
		const devicesLogin = platform === 'web' ? '' : platform;
		const localTime = moment(new Date()).format('MM/DD/YYYY HH:mm:ss');
		const timezoneOffset = moment(new Date()).format('Z');
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		let authReq = req.clone({
			url: `${this.apiUrl}${req.url}`,
			headers: req.headers
				.append('tokenCC', companyCode)
				.append('devices', devicesLogin)
				.append('localTime', localTime)
				.append('timezoneOffset', timezoneOffset)
				.append('timeZone', timeZone),
		});
		if (authorization) {
			authReq = req.clone({
				headers: req.headers
					.set('Authorization', authorization)
					.append('tokenCC', companyCode)
					.append('devices', devicesLogin)
					.append('localTime', localTime)
					.append('timezoneOffset', timezoneOffset)
					.append('timeZone', timeZone),
				url: `${this.apiUrl}${req.url}`,
			});
		}

		if (
			req.url.includes('file://') ||
			(req.url.includes('http') && !req.url.includes(this.chatSignalr))
		) {
			return next.handle(req).toPromise();
		} else if (req.url.includes(this.chatSignalr)) {
			const authRequest = req.clone({
				url: `${req.url}`,
				headers: req.headers
					.set('Authorization', authorization)
					.append('tokenCC', companyCode)
					.append('devices', devicesLogin)
					.append('localTime', localTime)
					.append('timezoneOffset', timezoneOffset)
					.append('timeZone', timeZone),
			});
			return next.handle(authRequest).toPromise();
		} else {
			// send the newly created request
			return next
				.handle(authReq)
				.pipe(
					tap(
						event => {
							// if (event instanceof HttpResponse) { }
						},
						error => {
							if (error.status === 403) {
								this.snackBar.open(error.error['Message']);
							}
							if (error.status === 401) {
								this.message.disconnect();
								sessionStorage.clear();
								sessionStorage.setItem(
									'showSplash',
									JSON.stringify(false)
								);
								this.router.navigate(['signin']);
							} else if (this.shouldShowGoogleAuthDialog(error, req)) {
								this.googleAuthDialog();
							}
						}
					)
				)
				.toPromise();
		}
	}

	private shouldShowGoogleAuthDialog(error: any, req: HttpRequest<any>): boolean {
		if (Capacitor.isNativePlatform()) return false;
		if (this.googleAuthDialogDismissed) return false;
		if (this.googleAuthDialogOpen) return false;

		const errorMessage = error?.error?.Message;
		if (errorMessage !== GOOGLE_AUTH_ERROR_MESSAGE) return false;

		const mailServer = localStorage.getItem('mailServer');
		if (mailServer && mailServer !== 'google') return false;

		const isEmailCalendarRequest = EMAIL_CALENDAR_URL_PATTERNS.some(
			pattern => req.url.includes(pattern)
		);
		if (!isEmailCalendarRequest) return false;

		return true;
	}

	private googleAuthDialog(): void {
		if (this.googleAuthDialogOpen) return;

		this.googleAuthDialogOpen = true;
		this.ggSync = this.authGoogleService.openDialog();
		this.ggSync.afterClosed().subscribe(result => {
			this.googleAuthDialogOpen = false;

			if (!result) {
				this.googleAuthDialogDismissed = true;
				return;
			}

			this.authGoogleService.getGooglePermisson().subscribe({
				next: (rs) => {
					if (rs !== this.GOOGLE_ALREADY_LOGGED) {
						const url = rs.toString();
						this.authGoogleService.openLinkInBrowser(
							url,
							(mess) => {
								if (mess === 'closed') {
									this.emailService.shouldRefreshEmailProfile(true);
								}
							}
						);
					}
				},
				error: (err) => {
					console.error('Google auth permission error:', err);
				},
			});
		});
	}
}
